import { CSSProperties, ReactNode } from 'react';
import { Avatar } from '../core/Avatar';
import { AlmaNote } from './AlmaNote';
import { PhotoGrid } from './PhotoGrid';

/**
 * HostChoiceCard — the full host proposal card on the Choose Host screen:
 * avatar + name + meta header, a 3×3 photo mosaic, Alma's note, a gold-ruled
 * "Why you'll connect" list, specialty pills, and the full-width CTA.
 *
 * state: 'default' | 'chosen' (success ring + badge, CTA turns success)
 *        | 'dimmed' (28% opacity, pointer-events off — the other card).
 */
export interface HostChoiceReason {
  /** Tabler icon name (without `ti-` prefix). Default 'map-pin'. */
  icon?: string;
  text: string;
}

export interface HostChoiceCardProps {
  /** Full name, e.g. "Matheus Silva". */
  name: string;
  /** Host's profile photo; falls back to an initial disc when absent. */
  avatarUrl?: string | null;
  /** The host's one-line self-description. */
  headline?: string | null;
  /** Used in the CTA ("Choose Matheus"); derived from name when omitted. */
  firstName?: string;
  city?: string;
  /** e.g. "Replies in ~2h". */
  responseTime?: string;
  /** Photo URLs for the mosaic; palette fallbacks fill the gaps. */
  photos?: (string | null | undefined)[];
  /** Alma's short note (quote marks auto-added). */
  almaNote?: string;
  reasons?: HostChoiceReason[];
  specialties?: string[];
  state?: 'default' | 'chosen' | 'dimmed';
  /** CTA label override. */
  ctaLabel?: string;
  /** Disables the CTA (e.g. while saving). */
  disabled?: boolean;
  onChoose?: () => void;
  /** Extra content between specialties and the CTA. */
  children?: ReactNode;
  style?: CSSProperties;
}

export function HostChoiceCard({
  name, avatarUrl, headline, firstName, city, responseTime,
  photos = [], almaNote, reasons = [], specialties = [],
  state = 'default', ctaLabel, disabled = false, onChoose,
  children, style,
}: HostChoiceCardProps) {
  const first = firstName ?? name.split(' ')[0];
  const chosen = state === 'chosen';
  const dimmed = state === 'dimmed';

  const rule = (label: string) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginTop: 28, marginBottom: 14 }}>
      <span style={{ color: 'rgb(var(--gold))', fontSize: 'var(--fs-caption)', fontWeight: 500, whiteSpace: 'nowrap' }}>{label}</span>
      <span style={{ flex: 1, height: 1, background: 'var(--line)' }} />
    </div>
  );

  return (
    <div
      onClick={!chosen && !dimmed ? onChoose : undefined}
      style={{
        flex: 1, minWidth: 0,
        background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 16,
        overflow: 'hidden', display: 'flex', flexDirection: 'column',
        cursor: !chosen && !dimmed && onChoose ? 'pointer' : 'default',
        transition: 'opacity .3s, border-color .3s, box-shadow .3s',
        fontFamily: 'var(--font-sans)',
        ...(chosen ? { borderColor: 'rgb(var(--success))', boxShadow: '0 0 0 1px rgb(var(--success))' } : null),
        ...(dimmed ? { opacity: 0.28, pointerEvents: 'none' as const } : null),
        ...style,
      }}
    >
      {/* Header */}
      <div style={{ padding: '20px 22px 18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <Avatar name={name} src={avatarUrl ?? undefined} size={52} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: 'var(--font-serif)', fontSize: 'calc(20px * var(--text-scale))', color: 'var(--text)', lineHeight: 1.2 }}>{name}</div>
            {headline ? (
              <div style={{ fontSize: 'var(--fs-caption)', color: 'var(--text-soft)', marginTop: 4, lineHeight: 1.4, textWrap: 'pretty' }}>
                {headline}
              </div>
            ) : null}
            <div style={{ fontSize: 'var(--fs-caption)', color: 'var(--text-dim)', marginTop: 5, display: 'flex', alignItems: 'center', gap: 10 }}>
              {city ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <i className="ti ti-map-pin" style={{ fontSize: 'calc(11px * var(--text-scale))' }} />{city}
                </span>
              ) : null}
              {responseTime ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <i className="ti ti-clock" style={{ fontSize: 'calc(11px * var(--text-scale))' }} />{responseTime}
                </span>
              ) : null}
            </div>
          </div>
          {chosen ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, borderRadius: 999, padding: '4px 10px', fontSize: 'var(--fs-caption2)', background: 'rgb(var(--success) / .11)', color: 'var(--success-fg)', border: '0.5px solid rgb(var(--success) / .28)', flexShrink: 0 }}>
              <span style={{ width: 4, height: 4, borderRadius: '50%', background: 'rgb(var(--success))' }} />Chosen
            </span>
          ) : null}
        </div>
      </div>

      {/* Photo mosaic */}
      <PhotoGrid images={photos} count={9} columns={3} gap={2} radius={0} alt={`${first}'s photo`} />

      {/* Body */}
      <div style={{ padding: '24px 24px 0', display: 'flex', flexDirection: 'column', flex: 1 }}>
        {almaNote ? <AlmaNote quote={almaNote} label={`Why Alma chose ${first}`} quoteSize={13.5} /> : null}

        {reasons.length ? (
          <>
            {rule("Why you'll connect")}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
              {reasons.map((r) => (
                <div key={r.text} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <i className={`ti ti-${r.icon ?? 'map-pin'}`} style={{ color: 'var(--text-dim)', fontSize: 'calc(12px * var(--text-scale))', marginTop: 2, flexShrink: 0 }} />
                  <span style={{ fontSize: 'var(--fs-footnote)', color: 'var(--text-soft)', lineHeight: 1.58, textWrap: 'pretty' }}>{r.text}</span>
                </div>
              ))}
            </div>
          </>
        ) : null}

        {specialties.length ? (
          <>
            {rule('Specialties')}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {specialties.map((s) => (
                <span key={s} style={{ fontSize: 'var(--fs-caption2)', fontWeight: 500, color: 'var(--text-soft)', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 999, padding: '4px 10px' }}>
                  {s}
                </span>
              ))}
            </div>
          </>
        ) : null}

        {children}
      </div>

      {/* CTA */}
      <div style={{ padding: '22px 24px 26px' }}>
        <button className="sk-hit"
          type="button"
          disabled={disabled}
          onClick={(e) => { e.stopPropagation(); onChoose?.(); }}
          style={{
            width: '100%', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-subhead)', fontWeight: 600,
            border: 'none', borderRadius: 999, padding: 14,
            cursor: disabled ? 'not-allowed' : 'pointer', transition: 'opacity .15s',
            background: chosen ? 'rgb(var(--success))' : 'var(--text)',
            color: chosen ? '#fff' : 'var(--bg)',
            opacity: disabled ? 0.6 : 1,
          }}
        >
          {ctaLabel ?? `Choose ${first}`}
        </button>
      </div>
    </div>
  );
}

export default HostChoiceCard;
